import {
  ChangeDetectionStrategy,
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Injector,
  NgZone,
  OnDestroy,
  OnInit,
  Signal,
  ViewChild,
  afterNextRender,
  computed,
  effect,
  input,
  linkedSignal,
  output,
  signal,
  untracked,
  viewChild,
  viewChildren,
} from '@angular/core';
import moment from 'moment';
import {
  IBase,
  IClient,
  IBlockPalette,
  IScheduleDay,
  IScheduleWeek,
  ITimelineAvailabilityLayout,
  ITimelineBlock,
  ITimelineColumn,
  ITimelineEdges,
  ITimelineLane,
  ITimelineRow,
  IScheduleCollapsedRows,
  IScheduleHoveredRow,
  ITimelineSegment,
  ITimelineTripChip,
  ITimelineVisibility,
  IVehicleSchedule,
  IVehicleScheduleAvailability,
  IVehicleScheduleChange,
  IVehicleScheduleConflict,
  IVehicleScheduleDrag,
  IVehicleScheduleDragFeedback,
  IVehicleScheduleEdit,
  IVehicleScheduleEditTarget,
  IVehicleScheduleFilters,
  IVehicleSchedulePendingEdit,
  IVehicleScheduleTrip,
  IVehicleScheduleTripAssignment,
  IVehicleScheduleTripDragOrigin,
} from 'src/app/models';
import { ScheduleAxis, ScheduleGroupBy, ScheduleViewMode, ScheduleViewOption } from 'src/app/models/enums';
import { APP } from 'src/app/config/app';
import { LiteralService, StorageService, UtilsService, VehiclesService } from 'src/app/services';
import { visibleSpanMinutes } from '../scheduleZoom';
import {
  editedTripSlot,
  fitsAvailability,
  mergeAvailabilityFragments,
  movedTo,
  placeScheduleChanges,
  recordScheduleEdit,
  recordTripMove,
  sameRoute,
  sameSlot,
  scheduleRowField,
} from '../scheduleChanges';
import { findScheduleConflicts, tripAssignments } from '../scheduleConflicts';
import {
  VehicleScheduleSaveModalComponent,
} from '../vehicle-schedule-save-modal/vehicle-schedule-save-modal.component';
import { placeStack, spansOverlap, stackLanes } from '../scheduleLayout';
import { clampShift, dragAxis, snapMinutes } from '../scheduleDrag';
import {
  addDays,
  buildScheduleDays,
  buildScheduleWeeks,
  daysBetween,
  formatWeekday,
  shiftPeriods,
  startOfDay,
  weekdayFromMonday,
} from '../scheduleDates';
import {
  buildTimelineColumns,
  buildTimelineSegments,
  endsCollapsed,
  hiddenRunsPerWeek,
  minutesToPx,
  pxToMinutes,
  sameSelection,
  segmentsWidth,
  startsCollapsed,
  visibleSpans,
} from '../timelineAxis';
import { images } from 'src/images';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';

@Component({
  selector: 'app-vehicle-schedule',
  templateUrl: './vehicle-schedule.component.html',
  styleUrls: ['./vehicle-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'relative', '(pointerleave)': 'hoveredRow.set(null)' },
  standalone: false
})
export class VehicleScheduleComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('timelineScroll') timelineScroll?: ElementRef<HTMLElement>;
  @ViewChild('timelineContent') timelineContent?: ElementRef<HTMLElement>;
  @ViewChild('timelineConfig') timelineConfig?: ElementRef<HTMLDetailsElement>;
  @ViewChild('periodStrip') periodStrip?: ElementRef<HTMLElement>;
  private readonly saveScheduleModal = viewChild.required(VehicleScheduleSaveModalComponent);

  readonly viewMode = input(ScheduleViewMode.DAY);
  readonly groupBy = input(ScheduleGroupBy.VEHICLES);
  readonly zoom = input(0);
  readonly searchValue = input('');
  readonly filters = input<IVehicleScheduleFilters>();
  readonly viewOptions = input(APP.SCHEDULE_DEFAULT_VIEW_OPTIONS);
  readonly visibleHours = input(APP.SCHEDULE_ALL_HOURS);
  readonly visibleDays = input(APP.SCHEDULE_ALL_WEEKDAYS);
  readonly selectedRowId = input<number>();
  readonly selectRowEventEmitter = output<number>();
  readonly changeTimelineVisibility = output<ITimelineVisibility>();

  readonly vehicleColumnWidth = APP.SCHEDULE_VEHICLE_COLUMN_WIDTH_PX;
  readonly headerHeight = APP.SCHEDULE_HEADER_HEIGHT_PX;
  readonly editorPositions = APP.SCHEDULE_EDITOR_POSITIONS;
  readonly TooltipPosition = TooltipPosition;

  public images = images;
  blockChipImage = computed(() => {
    return this.groupBy() === ScheduleGroupBy.VEHICLES ? images.driver : images.bus
  })

  selectedDayIndex = signal(0);
  selectedWeekIndex = signal(0);
  dragging = signal(false);
  timelineConfigOpened = signal(false);
  editTarget = signal<IVehicleScheduleEditTarget | undefined>(undefined);
  pendingEdit = signal<IVehicleSchedulePendingEdit | undefined>(undefined);
  pendingConflicts = computed(() => this.pendingEdit()?.conflicts ?? []);
  proposedDrop = signal<{ edit: IVehicleScheduleEdit; dropped: HTMLElement } | undefined>(undefined);
  proposedEdit = computed(() => this.proposedDrop()?.edit);
  checkingConflicts = signal(false);
  checkingTarget = signal(false);
  private fetchedAvailabilities = signal(new Map<number, IVehicleScheduleAvailability[]>());
  private targetCheck = 0;
  dragFeedback = signal<IVehicleScheduleDragFeedback | undefined>(undefined);
  private dragClientId = signal<number | undefined>(undefined);
  private tripDragOrigin = signal<IVehicleScheduleTripDragOrigin | undefined>(undefined);
  private tripDropTargets = computed(() => {
    const origin = this.tripDragOrigin();
    if (!origin) {
      return undefined;
    }
    return this.rows().map((row) => {
      const inOriginRow = row.schedule.id === origin.rowId;
      const acceptsOtherRows = origin.axis === ScheduleAxis.VERTICAL && row.schedule.active;
      const reachable = inOriginRow || (acceptsOtherRows && this.belongsToClient(row.schedule, origin.clientId));
      return row.blocks.map(
        (block) =>
          reachable &&
          sameRoute(block.availability, origin.trip) &&
          (origin.axis === ScheduleAxis.HORIZONTAL || fitsAvailability(block.availability, origin.trip)),
      );
    });
  });
  incompatibleRowReasons = computed(() => {
    const targets = this.tripDropTargets();
    const clientId = this.dragClientId();
    return this.rows().map((row, index) => {
      if (targets) {
        return targets[index].some(Boolean) ? undefined : 'vehicles.tooltips.noTripTarget';
      }
      return clientId !== undefined && !this.belongsToClient(row.schedule, clientId)
        ? 'vehicles.tooltips.otherClient'
        : undefined;
    });
  });
  dimmedBlocks = computed(() =>
    this.tripDropTargets()?.map((rowTargets) =>
      rowTargets.some(Boolean) ? rowTargets.map((target) => !target) : rowTargets.map(() => false),
    ),
  );
  droppedAvailabilityId = signal<number | undefined>(undefined);
  private conflictCheck = 0;
  private drag?: IVehicleScheduleDrag;
  private longPressTimer?: number;
  private autoScrollFrame?: number;
  private suppressClick = false;
  viewportWidth = signal(APP.SCHEDULE_DEFAULT_VIEWPORT_WIDTH);

  private rangeStart = computed(() => startOfDay(this.filters()?.from ?? new Date()));
  private rangeEnd = computed(() => (this.filters()?.to ? startOfDay(this.filters()!.to!) : undefined));
  private loadedPeriods = signal(APP.SCHEDULE_VISIBLE_PERIODS);
  days: Signal<IScheduleDay[]> = computed(() =>
    buildScheduleDays(this.rangeStart(), this.rangeEnd(), this.loadedPeriods(), this.literalService.currentLanguage()),
  );
  weeks: Signal<IScheduleWeek[]> = computed(() =>
    buildScheduleWeeks(this.rangeStart(), this.rangeEnd(), this.loadedPeriods(), this.literalService.currentLanguage()),
  );

  isWeekMode = computed(() => this.viewMode() === ScheduleViewMode.WEEK);
  activeDays = computed(() => (this.isWeekMode() ? this.visibleDays() : APP.SCHEDULE_ALL_WEEKDAYS));
  timelineConfigApplied = computed(
    () =>
      !sameSelection(this.visibleHours(), APP.SCHEDULE_ALL_HOURS) ||
      (this.isWeekMode() && !sameSelection(this.visibleDays(), APP.SCHEDULE_ALL_WEEKDAYS)),
  );
  visibleMinutesPerDay = computed(() => this.visibleHours().length * APP.MINUTES_PER_HOUR);
  hiddenRunsPerVisibleDay = computed(() => hiddenRunsPerWeek(this.visibleHours(), this.activeDays()) / this.activeDays().length);
  pxPerMinute = computed(() => {
    const span = visibleSpanMinutes(this.viewMode(), this.zoom(), this.visibleMinutesPerDay(), this.activeDays().length);
    const collapsedDays = span / this.visibleMinutesPerDay();
    const collapsedWidth = this.hiddenRunsPerVisibleDay() * collapsedDays * APP.SCHEDULE_COLLAPSED_HOURS_WIDTH_PX;
    return Math.max(this.viewportWidth() - collapsedWidth, this.viewportWidth() / 2) / span;
  });
  selectedPeriodIndex = computed(() => (this.isWeekMode() ? this.selectedWeekIndex() : this.selectedDayIndex()));
  axisStartMinutes = computed(() => {
    if (this.selectedPeriodIndex() > 0) {
      return -this.periodMinutes();
    }
    return this.viewMode() === ScheduleViewMode.DAY ? -APP.MINUTES_PER_HOUR : -APP.MINUTES_PER_DAY;
  });
  axisEndMinutes = computed(() => 2 * this.periodMinutes());
  periods: Signal<{ label: string }[]> = computed(() => (this.isWeekMode() ? this.weeks() : this.days()));
  periodStartDate = computed(() =>
    this.isWeekMode() ? this.weeks()[this.selectedWeekIndex()].startDate : this.days()[this.selectedDayIndex()].date,
  );
  periodMinutes = computed(() => (this.isWeekMode() ? APP.MINUTES_PER_WEEK : APP.MINUTES_PER_DAY));

  segments: Signal<ITimelineSegment[]> = computed(() => this.segmentsAt(this.pxPerMinute()));
  columns: Signal<ITimelineColumn[]> = computed(() =>
    buildTimelineColumns(
      this.segments(),
      this.isWeekMode() ? APP.MINUTES_PER_DAY : APP.MINUTES_PER_HOUR,
      this.periodMinutes(),
      (columnStart) => this.columnLabel(columnStart),
    ),
  );
  collapsedColumns = computed(() => this.columns().filter((column) => column.collapsed));
  timelineWidth = computed(() => segmentsWidth(this.segments()));
  leadInWidth = computed(() => this.toPx(0));

  private vehicleSchedules = signal<IVehicleSchedule[]>([]);
  private changes = signal(new Map<number, IVehicleScheduleChange>());
  changeList = computed(() => [...this.changes().values()]);
  pendingChanges = computed(() => this.changeList().map((change) => change.availability));
  unsavedChanges = computed(() => this.changeList().length > 0 && !this.saveScheduleModal().saveInProgress());
  private displayedChanges = computed(() => {
    const pending = this.pendingEdit();
    if (!pending?.dropped) {
      return this.changes();
    }
    const displayed = new Map(this.changes());
    pending.changes.forEach((change) => displayed.set(change.availability.id, change));
    return displayed;
  });

  schedules: Signal<IVehicleSchedule[]> = computed(() => {
    const search = this.searchValue().toLowerCase();
    return placeScheduleChanges(this.vehicleSchedules(), this.displayedChanges(), this.groupBy())
      .filter((schedule) => !search || schedule.name?.toLowerCase().includes(search))
      .map((schedule) => ({
        ...schedule,
        availabilities: schedule.availabilities.filter((availability) => this.isVisible(availability)),
      }));
  });

  readonly collapsedRows = input<IScheduleCollapsedRows>({ collapsed: [], expanded: [] });
  readonly collapsedRowsChange = output<IScheduleCollapsedRows>();
  private collapsedRowOverrides = linkedSignal<ReadonlyMap<number, boolean>>(() => {
    const { collapsed, expanded } = this.collapsedRows();
    return new Map([
      ...collapsed.map((rowId): [number, boolean] => [rowId, true]),
      ...expanded.map((rowId): [number, boolean] => [rowId, false]),
    ]);
  });
  private readonly scheduleRowElements = viewChildren<ElementRef<HTMLElement>>('scheduleRow');
  readonly hoveredRow = signal<IScheduleHoveredRow | null>(null);

  rows: Signal<ITimelineRow[]> = computed(() => {
    const hidesEmptyRows = this.viewOptions()[ScheduleViewOption.HIDE_EMPTY_ROWS];
    const collapsedRowOverrides = this.collapsedRowOverrides();
    const expandRowLabel = this.literalService.get('vehicles.actions.viewOptions.expandRow', true);
    const collapseRowLabel = this.literalService.get('vehicles.actions.viewOptions.collapseRow', true);
    return this.schedules().map((schedule) => {
      const row = this.buildRow(schedule);
      const collapsed = collapsedRowOverrides.get(schedule.id!) ?? (hidesEmptyRows && this.isEmptyRow(row));
      if (!collapsed) return { ...row, collapseLabel: `${collapseRowLabel}: ${schedule.name}` };
      return {
        ...row,
        height: APP.SCHEDULE_COLLAPSED_ROW_HEIGHT_PX,
        collapsed: true,
        expandLabel: `${expandRowLabel}: ${schedule.name}`,
      };
    });
  });

  collapseRow(rowId: number, event: Event) {
    event.stopPropagation();
    this.hoveredRow.set(null);
    this.setRowCollapsed(rowId, true);
  }

  onRowPointerEnter(row: ITimelineRow, event: PointerEvent) {
    if (row.collapsed) {
      this.hoveredRow.set(null);
      return;
    }
    const rowRect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const frameRect = this.elementRef.nativeElement.getBoundingClientRect();
    this.hoveredRow.set({
      id: row.schedule.id!,
      label: row.collapseLabel!,
      top: rowRect.top - frameRect.top + rowRect.height / 2,
    });
  }

  expandRow(rowId: number) {
    this.setRowCollapsed(rowId, false);
  }

  private setRowCollapsed(rowId: number, collapsed: boolean) {
    const overrides = new Map(this.collapsedRowOverrides()).set(rowId, collapsed);
    this.collapsedRowOverrides.set(overrides);
    const entries = [...overrides.entries()];
    this.collapsedRowsChange.emit({
      collapsed: entries.filter(([, rowCollapsed]) => rowCollapsed).map(([overriddenRowId]) => overriddenRowId),
      expanded: entries.filter(([, rowCollapsed]) => !rowCollapsed).map(([overriddenRowId]) => overriddenRowId),
    });
    afterNextRender(
      () => {
        const rowElement = this.scheduleRowElements().find(
          (element) => element.nativeElement.dataset['rowId'] === String(rowId),
        );
        rowElement?.nativeElement.focus();
      },
      { injector: this.injector },
    );
  }

  private isEmptyRow(row: ITimelineRow): boolean {
    if (this.availabilitiesShown()) return !row.blocks.length;
    return row.blocks.every((block) => !block.trips.length);
  }

  private nowTick = signal(new Date());
  private nowTimer = setInterval(() => this.nowTick.set(new Date()), 60000);
  readonly now = this.nowTick.asReadonly();

  nowLineOffset: Signal<number | null> = computed(() => {
    const minutes = (this.nowTick().getTime() - this.periodStartDate().getTime()) / 60000;
    if (minutes < this.axisStartMinutes() || minutes > this.axisEndMinutes()) {
      return null;
    }
    return this.toPx(minutes);
  });

  // the blocks slide only while zooming: on a period change their offset jumps a whole day or week
  zooming = signal(false);
  private previousZoom?: number;
  private zoomingTimer?: number;
  private zoomFrame?: number;
  private followFrame?: number;

  private previousViewMode?: ScheduleViewMode;
  private previousVisibleHours?: number[];
  private previousVisibleDays?: number[];
  private previousGroupBy?: ScheduleGroupBy;
  private previousFilters?: IVehicleScheduleFilters;
  private filtersTracked = false;
  private resizeObserver?: ResizeObserver;
  private dragStartX = 0;
  private dragStartScrollLeft = 0;
  private scrollbarHeld = false;
  private scrollSettleTimer?: number;
  private lastScrollLeft?: number;

  private page = 1;
  private lastPage = false;
  readonly skeletonLayout = APP.SCHEDULE_SKELETON_LAYOUT;
  clientIds = signal<number[]>([]);
  loadingAxis = signal<ScheduleAxis | undefined>(undefined);
  loading = computed(() => !!this.loadingAxis());
  private loadedWindow = signal<{ from: Date; to: Date } | undefined>(undefined);
  loadingSpan = computed<Pick<ITimelineSegment, 'left' | 'width'> | undefined>(() => {
    const loaded = this.loadedWindow();
    if (this.loadingAxis() !== ScheduleAxis.HORIZONTAL || !loaded || !this.schedules().length) {
      return undefined;
    }
    const axisStart = Math.max(this.axisStartMinutes(), this.toMinutes(this.earliestFrom()));
    const axisEnd = this.axisEndMinutes();
    const loadedStart = this.toMinutes(loaded.from);
    const loadedEnd = this.toMinutes(loaded.to);
    if (loadedEnd < axisEnd) {
      return this.pxSpan(Math.max(axisStart, loadedEnd), axisEnd);
    }
    if (loadedStart > axisStart) {
      return this.pxSpan(axisStart, Math.min(axisEnd, loadedStart));
    }
    return undefined;
  });
  skeletonRows = computed(() => {
    if (!this.schedules().length) {
      return this.skeletonLayout;
    }
    return this.loadingSpan() ? [] : this.skeletonLayout.slice(0, 1);
  });
  private requestedWindow?: { from: Date; to: Date };
  private requestGeneration = 0;

  availabilitiesShown = computed(
    () => this.viewOptions()[ScheduleViewOption.SHOW_AVAILABILITIES] || this.dragFeedback()?.tripId !== undefined,
  );
  axisFrom = computed(() => shiftPeriods(this.periodStartDate(), -1, this.isWeekMode()));
  axisTo = computed(() => shiftPeriods(this.periodStartDate(), 2, this.isWeekMode()));
  earliestFrom = computed(() => new Date(this.days()[0].date.getTime() - APP.MINUTES_PER_HOUR * 60000));
  windowFrom = computed(() => {
    const from = shiftPeriods(this.periodStartDate(), -1 - APP.SCHEDULE_PREFETCHED_PERIODS, this.isWeekMode());
    return from < this.earliestFrom() ? this.earliestFrom() : from;
  });
  windowTo = computed(() => shiftPeriods(this.periodStartDate(), 2 + APP.SCHEDULE_PREFETCHED_PERIODS, this.isWeekMode()));

  constructor(
    private vehiclesService: VehiclesService,
    private storageService: StorageService,
    public utilsService: UtilsService,
    public literalService: LiteralService,
    private injector: Injector,
    private ngZone: NgZone,
    private elementRef: ElementRef<HTMLElement>,
  ) {
    effect(() => {
      const mode = this.viewMode();
      if (this.previousViewMode && this.previousViewMode !== mode) {
        if (mode === ScheduleViewMode.WEEK) {
          this.selectedWeekIndex.set(Math.floor((this.selectedDayIndex() + weekdayFromMonday(this.rangeStart())) / 7));
        } else {
          const dayIndex = this.selectedWeekIndex() * 7 - weekdayFromMonday(this.rangeStart());
          this.loadedPeriods.update((count) => Math.max(count, dayIndex + 1));
          this.selectedDayIndex.set(Math.min(Math.max(0, dayIndex), this.days().length - 1));
        }
        this.scrollTo(this.leadInWidth());
        untracked(() => this.reloadWindow());
      }
      this.previousViewMode = mode;
    });

    effect(() => {
      const hours = this.visibleHours();
      const days = this.visibleDays();
      if (this.previousVisibleHours && (this.previousVisibleHours !== hours || this.previousVisibleDays !== days)) {
        untracked(() => this.scrollTo(this.leadInWidth()));
      }
      this.previousVisibleHours = hours;
      this.previousVisibleDays = days;
    });

    effect(() => {
      const groupBy = this.groupBy();
      if (this.previousGroupBy && this.previousGroupBy !== groupBy) {
        // the rows are a different list altogether, so the paging starts over instead of restoring the loaded ones
        untracked(() => {
          this.scrollToTop();
          this.reloadWindow(false);
        });
      }
      this.previousGroupBy = groupBy;
    });

    effect(() => {
      const zoom = this.zoom();
      if (this.previousZoom !== undefined && this.previousZoom !== zoom) {
        untracked(() => this.keepCenterWhileZooming());
        this.zooming.set(true);
        clearTimeout(this.zoomingTimer);
        this.zoomingTimer = window.setTimeout(() => this.zooming.set(false), APP.SCHEDULE_ZOOM_ANIMATION_MS);
      }
      this.previousZoom = zoom;
    });

    effect(() => {
      const filters = this.filters();
      if (this.filtersTracked && filters !== this.previousFilters) {
        untracked(() => {
          this.selectedDayIndex.set(0);
          this.selectedWeekIndex.set(0);
          this.loadedPeriods.set(APP.SCHEDULE_VISIBLE_PERIODS);
          this.vehicleSchedules.set([]);
          this.scrollToTop();
          this.scrollTo(this.leadInWidth());
          this.reloadWindow(false);
        });
      }
      this.filtersTracked = true;
      this.previousFilters = filters;
    });

    effect(() => {
      const from = this.axisFrom();
      const to = this.axisTo();
      const window = this.requestedWindow;
      // untracked: loading the page reads signals of its own, and they must not feed this effect back
      if (window && ((from < window.from && window.from > this.earliestFrom()) || to > window.to)) {
        untracked(() => this.reloadWindow());
      }
    });
  }

  async ngOnInit() {
    await Promise.all([this.loadNextPage(), this.loadClientIds()]);
  }

  private async loadClientIds() {
    const clients: IClient[] = JSON.parse((await this.storageService.getClients()) || '[]');
    this.clientIds.set(clients.map((client) => client.id!));
  }

  async reload() {
    await this.reloadWindow();
  }

  private async reloadWindow(keepLoadedRows = true) {
    // the new window has to bring back every vehicle already on screen, not just the first page of them
    const size = keepLoadedRows ? Math.max(1, this.page - 1) * APP.DEFAULT_PAGE_SIZE : APP.DEFAULT_PAGE_SIZE;
    this.page = 1;
    this.lastPage = false;
    // whatever is travelling belongs to the window we are leaving, so it no longer holds the loading slot
    this.loadingAxis.set(undefined);
    await this.loadNextPage(true, size);
  }

  private async loadNextPage(replaceRows = false, size = APP.DEFAULT_PAGE_SIZE) {
    if (this.loading() || (this.lastPage && !replaceRows)) {
      return;
    }
    const from = this.windowFrom();
    const to = this.windowTo();
    const generation = ++this.requestGeneration;
    this.requestedWindow = { from, to };
    this.loadingAxis.set(replaceRows ? ScheduleAxis.HORIZONTAL : ScheduleAxis.VERTICAL);
    try {
      const schedulePage = await this.vehiclesService.getVehicleSchedule(
        this.page,
        size,
        from,
        to,
        this.groupBy(),
        this.filters(),
      );
      // a period change while the request was travelling makes its rows belong to another window
      if (generation !== this.requestGeneration) {
        return;
      }
      // the rows are swapped only once the new window arrives, so the timeline never blinks empty
      this.vehicleSchedules.update((schedules) =>
        replaceRows ? schedulePage.content : [...schedules, ...schedulePage.content],
      );
      this.lastPage = schedulePage.last;
      // a reload asks for several pages at once, so the next one starts after all of them
      this.page += size / APP.DEFAULT_PAGE_SIZE;
      this.loadedWindow.set({ from, to });
    } finally {
      if (generation === this.requestGeneration) {
        this.loadingAxis.set(undefined);
        this.requestedWindow = this.loadedWindow();
      }
    }
  }

  ngAfterViewInit() {
    this.updateViewportWidth();
    const container = this.timelineScroll?.nativeElement;
    if (container && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.updateViewportWidth());
      this.resizeObserver.observe(container);
    }
    this.scrollTo(this.leadInWidth());
    this.lastScrollLeft = container?.scrollLeft;
  }

  ngOnDestroy() {
    clearInterval(this.nowTimer);
    clearTimeout(this.zoomingTimer);
    clearTimeout(this.scrollSettleTimer);
    cancelAnimationFrame(this.zoomFrame!);
    cancelAnimationFrame(this.followFrame!);
    this.stopDrag();
    this.stopWatchingScrollbar();
    this.resizeObserver?.disconnect();
  }

  private updateViewportWidth() {
    const container = this.timelineScroll?.nativeElement;
    const width = (container?.clientWidth ?? 0) - this.vehicleColumnWidth;
    if (width > 0) {
      this.viewportWidth.set(width);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (this.timelineConfigOpened() && !this.timelineConfig?.nativeElement.contains(event.target as Node)) {
      this.closeTimelineConfig();
    }
  }

  onTimelineConfigToggle() {
    this.timelineConfigOpened.set(!!this.timelineConfig?.nativeElement.open);
  }

  closeTimelineConfig() {
    if (this.timelineConfig) {
      this.timelineConfig.nativeElement.open = false;
    }
  }

  onApplyTimelineVisibility(visibility: ITimelineVisibility) {
    this.changeTimelineVisibility.emit(visibility);
    this.closeTimelineConfig();
  }

  selectPeriod(index: number) {
    if (this.isWeekMode()) {
      this.selectedWeekIndex.set(index);
    } else {
      this.selectedDayIndex.set(index);
    }
    this.scrollTo(this.leadInWidth());
  }

  onPeriodStripScroll() {
    const strip = this.periodStrip?.nativeElement;
    if (strip && strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - APP.SCHEDULE_SCROLL_LOAD_THRESHOLD_PX) {
      this.loadMorePeriods();
    }
  }

  private loadMorePeriods() {
    if (!this.rangeEnd()) {
      this.loadedPeriods.update((count) => count + APP.SCHEDULE_VISIBLE_PERIODS);
    }
  }

  goToNow() {
    this.selectPeriod(0);
    const container = this.timelineScroll?.nativeElement;
    const offset = this.nowLineOffset();
    if (container && offset !== null) {
      this.scrollTo(offset - container.clientWidth / 2);
    }
  }

  onTimelineScroll() {
    this.hoveredRow.set(null);
    const container = this.timelineScroll?.nativeElement;
    if (!container || this.drag?.axis) {
      return;
    }
    const scrollsVertically = container.scrollHeight > container.clientHeight;
    if (scrollsVertically && container.scrollTop + container.clientHeight >= container.scrollHeight - APP.SCHEDULE_SCROLL_LOAD_THRESHOLD_PX) {
      this.loadNextPage();
    }
    if (container.scrollLeft === this.lastScrollLeft) {
      return;
    }
    this.lastScrollLeft = container.scrollLeft;
    clearTimeout(this.scrollSettleTimer);
    if (!this.scrollbarHeld) {
      this.scrollSettleTimer = window.setTimeout(() => this.settleTimelineScroll(), APP.SCHEDULE_SCROLL_SETTLE_MS);
    }
  }

  onTimelineScrollPointerDown(event: PointerEvent) {
    if (event.target !== event.currentTarget || this.scrollbarHeld) {
      return;
    }
    this.scrollbarHeld = true;
    window.addEventListener('pointerup', this.onScrollbarRelease);
    window.addEventListener('pointercancel', this.onScrollbarRelease);
    window.addEventListener('pointermove', this.onScrollbarRelease);
  }

  private readonly onScrollbarRelease = (event: PointerEvent) => {
    if (event.type === 'pointermove' && event.buttons) {
      return;
    }
    this.stopWatchingScrollbar();
    clearTimeout(this.scrollSettleTimer);
    this.settleTimelineScroll();
  };

  private stopWatchingScrollbar() {
    this.scrollbarHeld = false;
    window.removeEventListener('pointerup', this.onScrollbarRelease);
    window.removeEventListener('pointercancel', this.onScrollbarRelease);
    window.removeEventListener('pointermove', this.onScrollbarRelease);
  }

  private settleTimelineScroll() {
    const container = this.timelineScroll?.nativeElement;
    if (!container || this.drag?.axis) {
      return;
    }
    const switchPoint = container.scrollLeft + this.viewportWidth() * APP.SCHEDULE_PERIOD_SWITCH_RATIO;
    const index = this.selectedPeriodIndex();
    if (switchPoint >= this.toPx(this.periodMinutes())) {
      if (index === this.periods().length - 1) {
        this.loadMorePeriods();
      }
      if (index < this.periods().length - 1) {
        this.shiftPeriod(1, container.scrollLeft);
      }
    } else if (switchPoint < this.toPx(0) && index > 0) {
      this.shiftPeriod(-1, container.scrollLeft);
    }
  }

  startHeaderDrag(event: PointerEvent) {
    const container = this.timelineScroll?.nativeElement;
    if (!container) {
      return;
    }
    this.dragging.set(true);
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = container.scrollLeft;
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  moveHeaderDrag(event: PointerEvent) {
    if (!this.dragging()) {
      return;
    }
    const container = this.timelineScroll?.nativeElement;
    if (container) {
      container.scrollLeft = this.dragStartScrollLeft - (event.clientX - this.dragStartX) * APP.SCHEDULE_DRAG_SPEED_MULTIPLIER;
    }
  }

  endHeaderDrag() {
    this.dragging.set(false);
  }

  toMinutes(dateTime: Date): number {
    return (dateTime.getTime() - this.periodStartDate().getTime()) / 60000;
  }

  private toPx(minutes: number): number {
    return minutesToPx(this.segments(), minutes);
  }

  private pxSpan(startMinutes: number, endMinutes: number): Pick<ITimelineSegment, 'left' | 'width'> {
    const left = this.toPx(startMinutes);
    return { left, width: this.toPx(endMinutes) - left };
  }

  private segmentsAt(pxPerMinute: number): ITimelineSegment[] {
    return buildTimelineSegments(this.axisStartMinutes(), this.axisEndMinutes(), this.visibleHours(), pxPerMinute, this.activeDays());
  }

  collapsedTooltip(column: ITimelineColumn): string {
    const start = this.utilsService.minutesToTime(column.startMinutes);
    const end = this.utilsService.minutesToTime(column.endMinutes);
    if (!this.isWeekMode()) {
      return `${start} - ${end}`;
    }
    return `${this.columnLabel(column.startMinutes)}, ${start} - ${this.columnLabel(column.endMinutes)}, ${end}`;
  }

  private columnLabel(columnStart: number): string {
    if (!this.isWeekMode()) {
      return this.utilsService.minutesToTime(columnStart);
    }
    const date = addDays(this.periodStartDate(), Math.floor(columnStart / APP.MINUTES_PER_DAY));
    return formatWeekday(date, this.literalService.currentLanguage());
  }

  private buildRow(schedule: IVehicleSchedule): ITimelineRow {
    const availabilities = schedule.availabilities.map((availability) => {
      const start = this.toMinutes(availability.startDateTime);
      const end = this.toMinutes(availability.endDateTime);
      return { availability, start, end, spans: visibleSpans(this.segments(), start, end) };
    });
    const sized = availabilities.map((item, index): ITimelineAvailabilityLayout => {
      const condensed = availabilities.some((other, otherIndex) => otherIndex !== index && spansOverlap(item.spans, other.spans));
      const trips = stackLanes(
        item.availability.trips.map((trip) => ({ startMinutes: this.toMinutes(trip.startDateTime), endMinutes: this.toMinutes(trip.endDateTime) })),
        condensed ? APP.SCHEDULE_TRIP_CHIP_MIN_HEIGHT_PX : APP.SCHEDULE_TRIP_CHIP_HEIGHT_PX,
      );
      const height = APP.SCHEDULE_BLOCK_LABEL_HEIGHT_PX + trips.height + (condensed ? 0 : APP.SCHEDULE_BLOCK_BOTTOM_PADDING_PX);
      return { ...item, trips, height };
    });
    const tops = placeStack(sized.map((item) => ({ startMinutes: item.start, spans: item.spans, height: item.height })));
    const blocks = sized.flatMap((item, index) => this.buildBlocks(item, APP.SCHEDULE_ROW_PADDING_PX + tops[index]));
    const contentHeight = Math.max(0, ...sized.map((item, index) => tops[index] + item.height));
    return {
      schedule,
      blocks,
      height: Math.max(APP.SCHEDULE_ROW_MIN_HEIGHT_PX, contentHeight + 2 * APP.SCHEDULE_ROW_PADDING_PX),
      collapsed: false,
    };
  }

  private buildBlocks(item: ITimelineAvailabilityLayout, top: number): ITimelineBlock[] {
    const { availability, start, end, spans, trips } = item;
    return spans.map((span, index) => ({
      key: `${availability.id}-${span.startMinutes}`,
      availability,
      left: span.left,
      width: span.width,
      top,
      height: item.height,
      trips: availability.trips.flatMap((trip, tripIndex) => this.buildTripChip(trip, span, trips.lanes[tripIndex])),
      tripsHeight: trips.height,
      labelled: index === 0,
      ...this.squaredEdges(start, end, index, spans.length),
    }));
  }

  private buildTripChip(trip: IVehicleScheduleTrip, span: ITimelineSegment, lane: ITimelineLane): ITimelineTripChip[] {
    const start = this.toMinutes(trip.startDateTime);
    const end = this.toMinutes(trip.endDateTime);
    const tripSpans = visibleSpans(this.segments(), start, end);
    const index = tripSpans.findIndex((tripSpan) => tripSpan.startMinutes >= span.startMinutes && tripSpan.endMinutes <= span.endMinutes);
    if (index < 0) {
      return [];
    }
    const tripSpan = tripSpans[index];
    return [
      {
        trip,
        left: tripSpan.left - span.left,
        width: tripSpan.width,
        top: lane.top,
        height: lane.height,
        compact: tripSpan.width < APP.SCHEDULE_MIN_TRIP_CHIP_WIDTH_PX,
        ...this.squaredEdges(start, end, index, tripSpans.length),
      },
    ];
  }

  private squaredEdges(start: number, end: number, index: number, count: number): ITimelineEdges {
    return {
      squaredStart: index === 0 && startsCollapsed(this.segments(), start),
      squaredEnd: index === count - 1 && endsCollapsed(this.segments(), end),
    };
  }

  chipLabel(trip: IVehicleScheduleTrip): string {
    const label = this.groupBy() === ScheduleGroupBy.DRIVERS ? trip.vehicle?.name : trip.driver?.name;
    return label ?? '';
  }

  blockBackground(palette: IBlockPalette, selected: boolean, inAssignedRow: boolean): string | null {
    if (selected) {
      return null;
    }
    return inAssignedRow ? palette.block : `color-mix(in srgb, ${palette.block} ${APP.SCHEDULE_COPY_BLOCK_OPACITY_PERCENT}%, transparent)`;
  }

  blockPalette(availability: IVehicleScheduleAvailability): IBlockPalette {
    const color = availability.service?.color;
    if (!color) return APP.SCHEDULE_NEUTRAL_PALETTE;
    const block = `color-mix(in srgb, ${color} 7%, transparent)`;
    return {
      block,
      blockHover: `color-mix(in srgb, ${block} 90%, black)`,
      border: block,
      chip: color,
      chipHover: `color-mix(in srgb, ${color} 90%, white)`,
      label: color,
    };
  }

  private isVisible(availability: IVehicleScheduleAvailability): boolean {
    return (
      this.toMinutes(availability.endDateTime) > this.axisStartMinutes() &&
      this.toMinutes(availability.startDateTime) < this.axisEndMinutes()
    );
  }

  private shiftPeriod(step: number, scrollLeft: number) {
    const anchorMinutes = step > 0 ? this.periodMinutes() : 0;
    const offset = scrollLeft - this.toPx(anchorMinutes);
    if (this.isWeekMode()) {
      this.selectedWeekIndex.update((index) => index + step);
    } else {
      this.selectedDayIndex.update((index) => index + step);
    }
    this.scrollTo(this.toPx(anchorMinutes - step * this.periodMinutes()) + offset);
    const index = this.selectedPeriodIndex();
    afterNextRender(() => this.revealPeriod(index), { injector: this.injector });
  }

  // the widths ease in over the animation, so the scroll follows the rendered scale frame by frame
  private keepCenterWhileZooming() {
    const container = this.timelineScroll?.nativeElement;
    const segments = this.renderedSegments();
    if (!container || !segments) {
      return;
    }
    const centerMinutes = pxToMinutes(segments, container.scrollLeft + this.viewportWidth() / 2);
    const startedAt = performance.now();
    cancelAnimationFrame(this.zoomFrame!);
    const follow = () => {
      const current = this.renderedSegments();
      if (current) {
        this.scrollTo(minutesToPx(current, centerMinutes) - this.viewportWidth() / 2);
      }
      if (performance.now() - startedAt < APP.SCHEDULE_ZOOM_ANIMATION_MS) {
        this.zoomFrame = requestAnimationFrame(follow);
      }
    };
    this.zoomFrame = requestAnimationFrame(follow);
  }

  private renderedSegments(): ITimelineSegment[] | undefined {
    const width = this.timelineContent?.nativeElement.getBoundingClientRect().width ?? 0;
    const segments = this.segments();
    const collapsed = segments.filter((segment) => segment.collapsed);
    const visibleMinutes = segments
      .filter((segment) => !segment.collapsed)
      .reduce((total, segment) => total + segment.endMinutes - segment.startMinutes, 0);
    const pxPerMinute = (width - this.vehicleColumnWidth - collapsed.length * APP.SCHEDULE_COLLAPSED_HOURS_WIDTH_PX) / visibleMinutes;
    return pxPerMinute > 0 ? this.segmentsAt(pxPerMinute) : undefined;
  }

  private revealPeriod(index: number) {
    this.periodStrip?.nativeElement.children[index]?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }

  private scrollToTop() {
    const container = this.timelineScroll?.nativeElement;
    if (container) {
      container.scrollTop = 0;
    }
  }

  private scrollTo(position: number) {
    const container = this.timelineScroll?.nativeElement;
    if (!container) {
      return;
    }
    container.scrollLeft = position;
  }

  onClickRow(rowId: number) {
    this.selectRowEventEmitter.emit(rowId);
  }

  openEditor(event: Event, availability: IVehicleScheduleAvailability, trip?: IVehicleScheduleTrip) {
    event.stopPropagation();
    if (this.suppressClick) {
      return;
    }
    this.editTarget.set({ origin: event.currentTarget as Element, availability, trip });
  }

  closeEditor() {
    const pending = this.pendingEdit();
    const dropped = this.proposedDrop()?.dropped;
    if (pending?.dropped) {
      this.revertDropPreview(pending);
    } else if (dropped) {
      this.releaseDragged(dropped, true);
    }
    this.pendingEdit.set(undefined);
    this.proposedDrop.set(undefined);
    this.conflictCheck++;
    this.checkingConflicts.set(false);
    this.targetCheck++;
    this.checkingTarget.set(false);
    this.fetchedAvailabilities.set(new Map());
    this.editTarget.set(undefined);
  }

  isDragged(availability: IVehicleScheduleAvailability, trip?: IVehicleScheduleTrip): boolean {
    const feedback = this.dragFeedback();
    return !!feedback && feedback.availabilityId === availability.id && feedback.tripId === trip?.id;
  }

  startItemDrag(event: PointerEvent, availability: IVehicleScheduleAvailability, trip?: IVehicleScheduleTrip) {
    event.stopPropagation();
    if (event.button !== 0 || this.drag) {
      return;
    }
    const container = this.timelineScroll?.nativeElement;
    const touch = event.pointerType === 'touch';
    const element = event.currentTarget as HTMLElement;
    const bounds = element.getBoundingClientRect();
    this.drag = {
      pointerId: event.pointerId,
      armed: !touch,
      element,
      availability,
      trip,
      startX: event.clientX,
      startY: event.clientY,
      clientX: event.clientX,
      clientY: event.clientY,
      startScrollLeft: container?.scrollLeft ?? 0,
      startScrollTop: container?.scrollTop ?? 0,
      grabOffsetY: event.clientY - (bounds.top + bounds.height / 2),
    };
    if (touch) {
      this.longPressTimer = window.setTimeout(() => this.drag && (this.drag.armed = true), APP.SCHEDULE_DRAG_LONG_PRESS_MS);
    }
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('pointermove', this.onDragMove);
      window.addEventListener('pointerup', this.onDragEnd);
      window.addEventListener('pointercancel', this.onDragCancel);
      window.addEventListener('touchmove', this.preventTouchScroll, { passive: false });
    });
  }

  private readonly preventTouchScroll = (event: TouchEvent) => {
    if (this.drag?.armed) {
      event.preventDefault();
    }
  };

  private readonly onDragMove = (event: PointerEvent) => {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    drag.clientX = event.clientX;
    drag.clientY = event.clientY;
    if (!drag.armed) {
      if (dragAxis(drag.clientX - drag.startX, drag.clientY - drag.startY, APP.SCHEDULE_DRAG_THRESHOLD_PX)) {
        this.stopDrag();
      }
      return;
    }
    this.updateDrag();
    this.autoScrollDrag();
  };

  private readonly onDragEnd = (event: PointerEvent) => {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    this.stopDrag();
    if (!drag.axis) {
      return;
    }
    this.suppressClick = true;
    window.setTimeout(() => (this.suppressClick = false));
    this.ngZone.run(() => {
      this.dragFeedback.set(undefined);
      this.dragClientId.set(undefined);
      this.tripDragOrigin.set(undefined);
      if (!drag.trip) {
        this.droppedAvailabilityId.set(drag.availability.id);
      }
      const target = { origin: drag.element, availability: drag.availability, trip: drag.trip };
      if (drag.edit && this.asksToReassignTrips(drag.edit, drag.availability, drag.trip)) {
        this.editTarget.set(target);
        this.proposedDrop.set({ edit: drag.edit, dropped: drag.element });
      } else if (drag.edit) {
        this.submitEdit(drag.edit, target, drag.element);
      } else {
        this.releaseDragged(drag.element, true);
      }
    });
  };

  private readonly onDragCancel = (event: PointerEvent) => {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    this.stopDrag();
    this.ngZone.run(() => {
      this.dragFeedback.set(undefined);
      this.dragClientId.set(undefined);
      this.tripDragOrigin.set(undefined);
    });
    if (drag.axis) {
      this.releaseDragged(drag.element, true);
    }
  };

  private asksToReassignTrips(edit: IVehicleScheduleEdit, availability: IVehicleScheduleAvailability, trip?: IVehicleScheduleTrip): boolean {
    const field = scheduleRowField(this.groupBy());
    return !trip && edit[field]?.id !== availability[field]?.id && !!this.currentAvailability(availability.id)?.trips.length;
  }

  private stopDrag() {
    clearTimeout(this.longPressTimer);
    cancelAnimationFrame(this.autoScrollFrame!);
    window.removeEventListener('pointermove', this.onDragMove);
    window.removeEventListener('pointerup', this.onDragEnd);
    window.removeEventListener('pointercancel', this.onDragCancel);
    window.removeEventListener('touchmove', this.preventTouchScroll);
    this.drag = undefined;
  }

  private updateDrag() {
    const drag = this.drag;
    const container = this.timelineScroll?.nativeElement;
    if (!drag || !container) {
      return;
    }
    const dx = drag.clientX - drag.startX + container.scrollLeft - drag.startScrollLeft;
    const dy = drag.clientY - drag.startY + container.scrollTop - drag.startScrollTop;
    if (!drag.axis) {
      drag.axis = dragAxis(dx, dy, APP.SCHEDULE_DRAG_THRESHOLD_PX);
      if (!drag.axis) {
        return;
      }
      if (drag.axis === ScheduleAxis.VERTICAL) {
        this.ngZone.run(() => this.dragClientId.set(drag.availability.managerClientId));
      }
      if (drag.trip) {
        this.ngZone.run(() => this.startTripDrag(drag, drag.trip!, drag.axis!));
      }
      this.draggedElements(drag.element).forEach((element) => element.classList.add('schedule-dragging'));
    }
    if (drag.axis === ScheduleAxis.HORIZONTAL) {
      this.dragHorizontally(drag, dx);
    } else {
      this.dragVertically(drag);
    }
  }

  private startTripDrag(drag: IVehicleScheduleDrag, trip: IVehicleScheduleTrip, axis: ScheduleAxis) {
    const rowId = drag.element.closest<HTMLElement>('[data-row-id]')?.dataset['rowId'];
    this.tripDragOrigin.set({
      trip,
      rowId: rowId === undefined ? undefined : Number(rowId),
      clientId: drag.availability.managerClientId,
      axis,
    });
  }

  private dragHorizontally(drag: IVehicleScheduleDrag, dx: number) {
    const item = drag.trip ?? drag.availability;
    const start = this.toMinutes(item.startDateTime);
    const snapped = snapMinutes(pxToMinutes(this.segments(), this.toPx(start) + dx), APP.SCHEDULE_DRAG_SNAP_MINUTES);
    const shift = clampShift(snapped - start, start, this.toMinutes(item.endDateTime), this.axisStartMinutes(), this.axisEndMinutes());
    drag.element.style.transform = `translate(${this.toPx(start + shift) - this.toPx(start)}px, 0px)`;
    this.setDragEdit(drag, shift);
  }

  private dragVertically(drag: IVehicleScheduleDrag) {
    const content = this.timelineContent?.nativeElement;
    const originRow = drag.element.closest<HTMLElement>('[data-row-id]');
    const blockCenterY = drag.clientY - drag.grabOffsetY;
    const rowElement = Array.from(content?.querySelectorAll<HTMLElement>('[data-row-id]') ?? []).find((element) => {
      const bounds = element.getBoundingClientRect();
      return blockCenterY >= bounds.top && blockCenterY < bounds.bottom;
    });
    if (!originRow || !rowElement) {
      return;
    }
    const offset = rowElement.getBoundingClientRect().top - originRow.getBoundingClientRect().top;
    drag.element.style.transform = `translate(0px, ${offset}px)`;
    const row = rowElement === originRow ? undefined : this.rows().find((item) => String(item.schedule.id) === rowElement.dataset['rowId']);
    this.setDragEdit(drag, 0, row);
  }

  private setDragEdit(drag: IVehicleScheduleDrag, shiftMinutes: number, row?: ITimelineRow) {
    const item = drag.trip ?? drag.availability;
    const shifted = (date: Date) => moment(date).add(shiftMinutes, 'minutes');
    const newStart = shifted(item.startDateTime);
    const edit: IVehicleScheduleEdit = {
      availabilityId: drag.availability.id,
      tripId: drag.trip?.id,
      driver: item.driver,
      vehicle: item.vehicle,
      startTime: newStart.format('HH:mm'),
      endTime: shifted(item.endDateTime).format('HH:mm'),
      shiftMinutes,
    };
    if (row) {
      edit[scheduleRowField(this.groupBy())] = { id: row.schedule.id, name: row.schedule.name };
    }
    const target = drag.trip ? this.findTripAvailability(edit) : undefined;
    const otherDay = !newStart.isSame(item.startDateTime, 'day');
    const clientId = drag.availability.managerClientId;
    const rowAccepted = !row || (row.schedule.active && this.belongsToClient(row.schedule, clientId));
    const valid = (!drag.trip || !!target) && rowAccepted;
    drag.edit = valid ? edit : undefined;
    const feedback: IVehicleScheduleDragFeedback = {
      availabilityId: drag.availability.id,
      tripId: drag.trip?.id,
      targetAvailabilityId: target?.id,
      invalid: !valid,
      label: otherDay
        ? `${formatWeekday(newStart.toDate(), this.literalService.currentLanguage())} · ${edit.startTime} - ${edit.endTime}`
        : `${edit.startTime} - ${edit.endTime}`,
    };
    const previous = this.dragFeedback();
    if (JSON.stringify(previous) !== JSON.stringify(feedback)) {
      this.ngZone.run(() => this.dragFeedback.set(feedback));
    }
  }

  private autoScrollDrag() {
    cancelAnimationFrame(this.autoScrollFrame!);
    const drag = this.drag;
    const container = this.timelineScroll?.nativeElement;
    if (!drag?.axis || !container) {
      return;
    }
    const bounds = container.getBoundingClientRect();
    const scrollX =
      drag.axis === ScheduleAxis.HORIZONTAL
        ? this.edgeScroll(drag.clientX, bounds.left + this.vehicleColumnWidth, bounds.left + container.clientWidth)
        : 0;
    const scrollY =
      drag.axis === ScheduleAxis.VERTICAL ? this.edgeScroll(drag.clientY, bounds.top + this.headerHeight, bounds.top + container.clientHeight) : 0;
    if (!scrollX && !scrollY) {
      return;
    }
    this.autoScrollFrame = requestAnimationFrame(() => {
      this.scrollTo(container.scrollLeft + scrollX);
      container.scrollTop += scrollY;
      this.updateDrag();
      this.autoScrollDrag();
    });
  }

  private edgeScroll(position: number, min: number, max: number): number {
    if (position < min + APP.SCHEDULE_DRAG_AUTO_SCROLL_EDGE_PX) {
      return -APP.SCHEDULE_DRAG_AUTO_SCROLL_STEP_PX;
    }
    if (position > max - APP.SCHEDULE_DRAG_AUTO_SCROLL_EDGE_PX) {
      return APP.SCHEDULE_DRAG_AUTO_SCROLL_STEP_PX;
    }
    return 0;
  }

  private draggedElements(element: HTMLElement): HTMLElement[] {
    const block = element.parentElement?.closest<HTMLElement>('[data-availability-id]');
    return block ? [element, block] : [element];
  }

  private releaseDragged(element: HTMLElement, animateBack: boolean) {
    const offset = element.style.transform;
    element.style.transform = '';
    this.draggedElements(element).forEach((item) => item.classList.remove('schedule-dragging'));
    this.droppedAvailabilityId.set(undefined);
    if (!animateBack || !offset) {
      return;
    }
    const keyframes = [{ transform: offset }, { transform: 'none' }];
    const options = { duration: APP.SCHEDULE_MOVE_ANIMATION_MS, easing: 'ease-in-out' };
    element.animate(keyframes, options);
    const availabilityId = element.dataset['availabilityId'];
    if (availabilityId) {
      afterNextRender(
        () =>
          Array.from(element.parentElement?.querySelectorAll<HTMLElement>(`[data-availability-outline-id="${availabilityId}"]`) ?? [])
            .filter((outline) => outline.style.left === element.style.left)
            .forEach((outline) => outline.animate(keyframes, options)),
        { injector: this.injector },
      );
    }
  }

  isAvailabilitySelected(availability: IVehicleScheduleAvailability): boolean {
    const target = this.editTarget();
    return !target?.trip && target?.availability.id === availability.id;
  }

  isTripSelected(trip: IVehicleScheduleTrip): boolean {
    return this.editTarget()?.trip?.id === trip.id;
  }

  availabilityChanged(availability: IVehicleScheduleAvailability): boolean {
    return !!this.changes().get(availability.id)?.availabilityChanged;
  }

  inAssignedRow(availability: IVehicleScheduleAvailability, rowId?: number): boolean {
    const assignedRowId = availability[scheduleRowField(this.groupBy())]?.id;
    return assignedRowId === undefined || assignedRowId === rowId;
  }

  tripChanged(availability: IVehicleScheduleAvailability, trip: IVehicleScheduleTrip): boolean {
    return !!this.changes().get(availability.id)?.changedTripIds.includes(trip.id!);
  }

  readonly findTripAvailability = (edit: IVehicleScheduleEdit): IVehicleScheduleAvailability | undefined => {
    const located = this.locateTrip(edit.tripId);
    if (!located) {
      return undefined;
    }
    const field = scheduleRowField(this.groupBy());
    const rowId = movedTo(located.trip[field], edit[field])?.id ?? located.rowId;
    const editedTrip = editedTripSlot(located.trip, edit);
    const loaded = this.schedules().find((schedule) => schedule.id === rowId)?.availabilities ?? [];
    const fetched = (this.fetchedAvailabilities().get(rowId!) ?? [])
      .filter((availability) => !loaded.some((item) => item.id === availability.id))
      .map((availability) => this.changes().get(availability.id)?.availability ?? availability);
    const candidates = [...loaded, ...fetched].filter(
      (availability) => fitsAvailability(availability, editedTrip) && sameRoute(availability, located.trip),
    );
    return candidates.find((availability) => availability.id === edit.availabilityId) ?? candidates[0];
  };

  async checkTripTarget(edit: IVehicleScheduleEdit) {
    const located = this.locateTrip(edit.tripId);
    const field = scheduleRowField(this.groupBy());
    const resource = located && movedTo(located.trip[field], edit[field]);
    if (!located || !resource) {
      return;
    }
    const day = startOfDay(editedTripSlot(located.trip, edit).startDateTime);
    const check = ++this.targetCheck;
    this.checkingTarget.set(true);
    try {
      const content = await this.fetchSchedule({ groupBy: this.groupBy(), resource, day });
      if (check !== this.targetCheck) {
        return;
      }
      const availabilities = content
        .flatMap((schedule) => schedule.availabilities)
        .map((availability) => ({ ...availability, [field]: availability[field] ?? resource }));
      this.fetchedAvailabilities.update((fetched) => new Map(fetched).set(resource.id!, availabilities));
    } catch (error) {
      console.error('Could not check the availabilities of the new assignment', error);
    } finally {
      if (check === this.targetCheck) {
        this.checkingTarget.set(false);
      }
    }
  }

  private belongsToClient(schedule: IVehicleSchedule, clientId?: number): boolean {
    return clientId === undefined || schedule.clientId === undefined || schedule.clientId === clientId;
  }

  private locateTrip(tripId?: number): { rowId?: number; trip: IVehicleScheduleTrip } | undefined {
    const row = this.rows().find((item) => item.blocks.some((block) => block.trips.some((chip) => chip.trip.id === tripId)));
    const trip = row?.blocks.flatMap((block) => block.trips).find((chip) => chip.trip.id === tripId)?.trip;
    return row && trip ? { rowId: row.schedule.id, trip } : undefined;
  }

  async onSaveEdit(edit: IVehicleScheduleEdit) {
    const target = this.editTarget();
    const dropped = this.proposedDrop()?.dropped;
    if (target) {
      await this.submitEdit(edit, target, dropped);
    }
    this.proposedDrop.set(undefined);
  }

  private async submitEdit(edit: IVehicleScheduleEdit, target: IVehicleScheduleEditTarget, dropped?: HTMLElement) {
    const changes = this.recordEdit(edit);
    if (!changes.length) {
      if (dropped) {
        this.releaseDragged(dropped, true);
      }
      this.closeEditor();
      return;
    }
    const check = ++this.conflictCheck;
    this.checkingConflicts.set(true);
    const conflicts = await this.findConflicts(changes);
    if (check !== this.conflictCheck) {
      return;
    }
    this.checkingConflicts.set(false);
    if (conflicts.length) {
      const pending = { edit, changes, conflicts, dropped };
      if (dropped) {
        this.previewDrop(pending, target);
      } else {
        this.editTarget.set(target);
        this.pendingEdit.set(pending);
      }
      return;
    }
    this.commitEdit(edit, changes, target.origin);
  }

  private previewDrop(pending: IVehicleSchedulePendingEdit, target: IVehicleScheduleEditTarget) {
    const dropped = pending.dropped!;
    const from = dropped.getBoundingClientRect();
    const originRowId = dropped.closest('[data-row-id]')?.getAttribute('data-row-id') ?? undefined;
    const edited = pending.changes[pending.changes.length - 1].availability;
    this.releaseDragged(dropped, false);
    this.pendingEdit.set(pending);
    afterNextRender(
      () => {
        if (this.pendingEdit() !== pending) {
          return;
        }
        const [element] = this.editedElements(pending.edit, edited, originRowId);
        this.editTarget.set({ ...target, origin: element ?? dropped });
        this.revealEditedItem(pending.edit, edited, from, originRowId);
      },
      { injector: this.injector },
    );
  }

  private revertDropPreview(pending: IVehicleSchedulePendingEdit) {
    const origin = this.editTarget()?.origin;
    const original = this.currentAvailability(pending.edit.availabilityId);
    if (!origin || !original) {
      return;
    }
    const from = origin.getBoundingClientRect();
    afterNextRender(() => this.revealEditedItem(pending.edit, original, from), { injector: this.injector });
  }

  confirmConflicts() {
    const pending = this.pendingEdit();
    if (pending) {
      this.commitEdit(pending.edit, pending.changes, pending.dropped ? undefined : this.editTarget()?.origin);
    }
  }

  cancelConflicts() {
    if (this.pendingEdit()?.dropped) {
      this.closeEditor();
    } else {
      this.pendingEdit.set(undefined);
    }
  }

  private commitEdit(edit: IVehicleScheduleEdit, changes: IVehicleScheduleChange[], origin?: Element) {
    this.changes.update((current) => {
      const updated = new Map(current);
      changes.forEach((change) => updated.set(change.availability.id, change));
      return updated;
    });
    if (origin) {
      const from = origin.getBoundingClientRect();
      const originRowId = origin.closest('[data-row-id]')?.getAttribute('data-row-id') ?? undefined;
      const edited = changes[changes.length - 1].availability;
      if (origin instanceof HTMLElement) {
        this.releaseDragged(origin, false);
      }
      afterNextRender(() => this.revealEditedItem(edit, edited, from, originRowId), { injector: this.injector });
    }
    this.pendingEdit.set(undefined);
    this.closeEditor();
  }

  private editedTrips(changes: IVehicleScheduleChange[]): IVehicleScheduleTrip[] {
    return changes.flatMap((change) => {
      const current = this.currentAvailability(change.availability.id)?.trips ?? [];
      return change.availability.trips.filter((trip) => {
        const before = current.find((item) => item.id === trip.id);
        return !before || !sameSlot(before, trip);
      });
    });
  }

  private async findConflicts(changes: IVehicleScheduleChange[]): Promise<IVehicleScheduleConflict[]> {
    const editedTripIds = new Set(this.editedTrips(changes).map((trip) => trip.id));
    if (!editedTripIds.size) {
      return [];
    }
    const upcoming = new Map(this.changes());
    changes.forEach((change) => upcoming.set(change.availability.id, change));
    const local = tripAssignments(placeScheduleChanges(this.vehicleSchedules(), upcoming, this.groupBy()), this.groupBy());
    const checked = local.filter((assignment) => editedTripIds.has(assignment.trip.id));
    const pendingTripIds = new Set([...upcoming.values()].flatMap((change) => change.availability.trips.map((trip) => trip.id)));
    try {
      const fetched = await Promise.all(this.conflictQueries(checked).map((query) => this.fetchAssignments(query)));
      const candidates = [...fetched.flat().filter((assignment) => !pendingTripIds.has(assignment.trip.id)), ...local];
      return findScheduleConflicts(checked, candidates);
    } catch (error) {
      console.error('Could not check the schedule conflicts', error);
      return [];
    }
  }

  private conflictQueries(checked: IVehicleScheduleTripAssignment[]): { groupBy: ScheduleGroupBy; resource: IBase; day: Date }[] {
    const queries = checked.flatMap(({ trip, driver, vehicle }) => {
      const day = startOfDay(trip.startDateTime);
      return [
        ...(vehicle?.id !== undefined ? [{ groupBy: ScheduleGroupBy.VEHICLES, resource: vehicle, day }] : []),
        ...(driver?.id !== undefined ? [{ groupBy: ScheduleGroupBy.DRIVERS, resource: driver, day }] : []),
      ];
    });
    const key = (query: { groupBy: ScheduleGroupBy; resource: IBase; day: Date }) => `${query.groupBy}-${query.resource.id}-${query.day.getTime()}`;
    return queries.filter((query, index) => queries.findIndex((item) => key(item) === key(query)) === index);
  }

  private async fetchAssignments(query: { groupBy: ScheduleGroupBy; resource: IBase; day: Date }) {
    return tripAssignments(await this.fetchSchedule(query), query.groupBy);
  }

  private async fetchSchedule({ groupBy, resource, day }: { groupBy: ScheduleGroupBy; resource: IBase; day: Date }): Promise<IVehicleSchedule[]> {
    const filters: IVehicleScheduleFilters = {
      clients: [],
      operators: [],
      types: [],
      serviceTypes: [],
      zones: [],
      services: [],
      routes: [],
      vehicles: groupBy === ScheduleGroupBy.VEHICLES ? [resource] : [],
      drivers: groupBy === ScheduleGroupBy.DRIVERS ? [resource] : [],
    };
    const page = await this.vehiclesService.getVehicleSchedule(1, APP.DEFAULT_PAGE_SIZE, day, addDays(day, 1), groupBy, filters);
    return page.content;
  }

  private recordEdit(edit: IVehicleScheduleEdit): IVehicleScheduleChange[] {
    const source = this.currentAvailability(edit.availabilityId);
    if (!source) {
      return [];
    }
    const target = edit.tripId !== undefined ? this.findTripAvailability(edit) : undefined;
    if (target && target.id !== source.id) {
      const destination = this.currentAvailability(target.id) ?? target;
      return recordTripMove(source, destination, edit, this.changes().get(source.id), this.changes().get(target.id));
    }
    const previous = this.changes().get(edit.availabilityId);
    const change = recordScheduleEdit(source, edit, previous);
    return change && change !== previous ? [change] : [];
  }

  private currentAvailability(id: number): IVehicleScheduleAvailability | undefined {
    return this.changes().get(id)?.availability ?? this.loadedAvailability(id);
  }

  private editedElements(edit: IVehicleScheduleEdit, availability: IVehicleScheduleAvailability, originRowId?: string): HTMLElement[] {
    const content = this.timelineContent?.nativeElement;
    if (!content) {
      return [];
    }
    if (edit.tripId !== undefined) {
      return Array.from(content.querySelectorAll<HTMLElement>(`[data-trip-id="${edit.tripId}"]`));
    }
    const blocksIn = (rowId?: number | string) =>
      Array.from(
        content.querySelectorAll<HTMLElement>(
          `[data-row-id="${rowId}"] [data-availability-id="${edit.availabilityId}"], [data-row-id="${rowId}"] [data-availability-outline-id="${edit.availabilityId}"]`,
        ),
      );
    const assignedRowBlocks = blocksIn(availability[scheduleRowField(this.groupBy())]?.id);
    return assignedRowBlocks.length ? assignedRowBlocks : blocksIn(originRowId);
  }

  private followEditedItem(edit: IVehicleScheduleEdit, availability: IVehicleScheduleAvailability, from: DOMRect, originRowId?: string) {
    const trip = edit.tripId !== undefined ? availability.trips.find((item) => item.id === edit.tripId) : undefined;
    const index = this.periodIndexOf((trip ?? availability).startDateTime);
    if (index === this.selectedPeriodIndex()) {
      return;
    }
    this.selectPeriod(index);
    afterNextRender(
      () => {
        this.revealPeriod(index);
        this.revealEditedItem(edit, availability, from, originRowId);
      },
      { injector: this.injector },
    );
  }

  private periodIndexOf(date: Date): number {
    const firstPeriod = this.isWeekMode() ? this.weeks()[0].startDate : this.days()[0].date;
    const days = daysBetween(firstPeriod, startOfDay(date));
    const index = Math.max(0, this.isWeekMode() ? Math.floor(days / 7) : days);
    if (!this.rangeEnd()) {
      this.loadedPeriods.update((count) => Math.max(count, index + 1));
    }
    return Math.min(index, this.periods().length - 1);
  }

  private revealEditedItem(edit: IVehicleScheduleEdit, availability: IVehicleScheduleAvailability, from: DOMRect, originRowId?: string) {
    const elements = this.editedElements(edit, availability, originRowId);
    if (!elements.length) {
      this.followEditedItem(edit, availability, from, originRowId);
      return;
    }
    const to = elements[0].getBoundingClientRect();
    const offset = `translate(${from.left - to.left}px, ${from.top - to.top}px)`;
    elements.forEach((element) =>
      element.animate([{ transform: offset }, { transform: 'none' }], { duration: APP.SCHEDULE_MOVE_ANIMATION_MS, easing: 'ease-in-out' }),
    );
    this.followWhileMoving(elements[0]);
  }

  private followWhileMoving(element: HTMLElement) {
    const container = this.timelineScroll?.nativeElement;
    if (!container) {
      return;
    }
    const startedAt = performance.now();
    cancelAnimationFrame(this.followFrame!);
    const follow = () => {
      this.keepInView(container, element.getBoundingClientRect());
      if (performance.now() - startedAt < APP.SCHEDULE_MOVE_ANIMATION_MS) {
        this.followFrame = requestAnimationFrame(follow);
      }
    };
    this.followFrame = requestAnimationFrame(follow);
  }

  private keepInView(container: HTMLElement, item: DOMRect) {
    const bounds = container.getBoundingClientRect();
    const padding = APP.SCHEDULE_FOLLOW_PADDING_PX;
    const left = bounds.left + this.vehicleColumnWidth + padding;
    const right = bounds.left + container.clientWidth - padding;
    const top = bounds.top + this.headerHeight + padding;
    const bottom = bounds.top + container.clientHeight - padding;
    const dx = this.overflowBy(item.left, item.right, left, right);
    const dy = this.overflowBy(item.top, item.bottom, top, bottom);
    if (dx) {
      this.scrollTo(container.scrollLeft + dx);
    }
    if (dy) {
      container.scrollTop += dy;
    }
  }

  private overflowBy(start: number, end: number, min: number, max: number): number {
    if (start < min) {
      return start - min;
    }
    return Math.max(0, end - max);
  }

  private loadedAvailability(id: number): IVehicleScheduleAvailability | undefined {
    const fragments = this.vehicleSchedules().flatMap((schedule) => schedule.availabilities.filter((availability) => availability.id === id));
    return mergeAvailabilityFragments(fragments);
  }

  discardChanges() {
    this.changes.set(new Map());
  }

  saveChanges() {
    this.saveScheduleModal().showSaveScheduleModal();
  }

  async onScheduleSummaryClosed() {
    this.discardChanges();
    await this.reload();
  }
}
